import './App.less';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useParams
} from "react-router-dom";

import AppRoot from "./pages/app/AppRoot";
import ProjectPageLayout from "./pages/home/ProjectPageLayout";
import { isNewUI } from "./v2/flag";
import { starterAddLink } from "./v2/data/templates";
import EnvironmentRoot from "./pages/environment/EnvironmentRoot";
import Templates from "./pages/template/Templates";
import CreateFromTemplate from "./pages/template/CreateFromTemplate";
import EnvironmentSettingsGeneral from './pages/environment/EnvironmentSettingsGeneral';
import ProjectRoot from './pages/project/ProjectRoot';
import Login from './pages/auth/Login';
import AuthRoot from './pages/auth/AuthRoot';
import Signup from './pages/auth/Signup';
import SetPassword from './pages/auth/SetPassword';
import EmailVerify from './pages/auth/EmailVerified';
import PrivateOutlet from './components/auth/PrivateOutlet';
import PublicOutlet from './components/auth/PublicOutlet';
import ResetPassword from './pages/auth/ResetPassword';
import ResetPasswordRequest from './pages/auth/ResetPasswordRequest';
import ActivationRequest from './pages/auth/ActivationRequest';
import ProjectIntegrationsRoot from './pages/integrations/ProjectIntegrationsRoot';
import ProjectSettingsRoot from './pages/project/ProjectSettingsRoot';
import ProjectSettingsApiKeys from './pages/project/ProjectSettingsApiKeys';
import ProjectEnvironments from './pages/project/ProjectEnvironments';
import EnvironmentSettingsRoot from './pages/environment/EnvironmentSettingsRoot';
import ProjectNew from './pages/project/ProjectNew';
import PricingPlans from './pages/billing/PricingPlans';
import AppSettingsRoot from './pages/app/AppSettingsRoot';
import AppSettingsEnvironmentVars from './pages/app/AppSettingsEnvironmentVars';
import AppSettingsScaling from './pages/app/AppSettingsScaling';
import AppSettingsDeployments from './pages/app/AppSettingsDeployments';
import AppSettingsDangerZone from './pages/app/AppSettingsDangerZone';
import AppPipelineBuilds from './pages/app/AppPipelineBuilds';
import ProjectSettingsGeneral from './pages/project/ProjectSettingsGeneral';
import ProjectSettingsMembers from './pages/project/ProjectSettingsMembers';
import ProjectSettingsBilling from './pages/project/ProjectSettingsBilling';
import ProjectIntegrationsGit from './pages/integrations/ProjectIntegrationsGit';
import ProjectIntegrationsCloud from './pages/integrations/ProjectIntegrationsCloud';
import ProjectIntegrationsContainer from './pages/integrations/ProjectIntegrationsContainer';
import ProjectIntegrationsSlack from './pages/integrations/ProjectIntegrationsSlack';
import ResourceSettingsRoot from './pages/resource/ResourceSettingsRoot';
import ResourceSettingsGeneral from './pages/resource/ResourceSettingsGeneral';
import ResourceSettingsDangerZone from './pages/resource/ResourceSettingsDangerZone';
import AppSettingsPipeline from './pages/app/AppSettingsPipeline';
import ResourceSettingsPipeline from './pages/resource/ResourceSettingsPipeline';
import { env } from "./env";
// import EnvironmentInfraDeploymentDetails from './pages/environment/EnvironmentInfraDeploymentDetails';
// import EnvironmentAppDeploymentDetails from './pages/environment/EnvironmentAppDeploymentDetails';
import EnvironmentRunningTemplates from './pages/environment/EnvironmentRunningTemplates';
import { notification } from 'antd';
import Welcome from './pages/home/Welcome';
import ProjectSettingsUsage from './pages/project/ProjectSettingsUsage';
import MetaTag from './components/MetaTag';
import ProjectsRoot from './pages/projects/ProjectsRoot';
import ProjectsOverview from './pages/projects/ProjectsOverview';
import AppSettingsCustomDomain from './pages/app/AppSettingsCustomDomain';
import ResourceSettingsCustomDomain from './pages/resource/ResourceSettingsCustomDomain';
import PipelineBuildDetails from './components/PipelineBuildDetails';
import PageNotFound from './pages/home/PageNotFound';
import ResourceSettingsScaling from './pages/resource/ResourceSettingsScaling';
import ResourceSettingsAlarms from './pages/resource/ResourceSettingsAlarms';
import ResourceSettingsStorage from './pages/resource/ResourceSettingsStorage';
import ResourceSettingsVariables from './pages/resource/ResourceSettingsVariables';
import { setDefaultPlan } from './utils/local-storage';
import EnvironmentOverview from './pages/environment/EnvironmentOverview';
import EnvironmentKubernetesAppDetails from './pages/environment/EnvironmentKubernetesAppDetails';
import EnvironmentComponentDetails from './pages/environment/EnvironmentComponentDetails';
import HomePageLayout from './pages/home/HomePageLayout';
import ProjectPipelines from './pages/project/ProjectPipelines';
import CostManagement from './pages/cost/CostManagement';
import { lazy, Suspense, useEffect, type ComponentType, type ReactElement } from 'react';
import EnvironmentFargateAppDetails from './pages/environment/EnvironmentFargateAppDetails';
import ProjectMonitoring from './pages/project/ProjectMonitoring';
import ProjectAlarms from './pages/project/ProjectAlarms';
import EnvironmentDeployApp from './pages/environment/EnvironmentDeployApp';
import EnvironmentDeployComponent from './pages/environment/EnvironmentDeployComponent';
import KubernetesSettingsGeneral from './pages/resource/KubernetesSettingsGeneral';
import KubernetesSettingsRoot from './pages/resource/KubernetesSettingsRoot';
import KubernetesSettingsDangerZone from './pages/resource/KubernetesSettingsDangerZone';
import TemplateRoot from './pages/template/TemplateRoot';
import EnvironmentSettingsDangerZone from './pages/environment/EnvironmentSettingsDangerZone';
import ClusterMonitoringSettings from './pages/resource/ClusterMonitoringSettings';
import ProjectMonitoringLogin from './pages/project/ProjectMonitoringLogin';
import TemplateComponentDetails from './pages/template/TemplateComponentDetails';
import TemplateAppDetails from './pages/template/TemplateAppDetails';
import ProjectCreateAlarm from './pages/project/ProjectCreateAlarm';
import EnvironmentSettingsClone from './pages/environment/EnvironmentSettingsClone';
import EnvironmentPreviewPlayground from './pages/environment/EnvironmentPreviewPlayground';
import AccountSecurity from './pages/account/AccountSecurity';
import ProjectSettingsSecurity from './pages/project/ProjectSettingsSecurity';


// The new UI is opt-in per user (see src/v2/flag.ts) and loaded only when enabled
const NewProjectShell = lazy(() => import("./v2/shell/ProjectShell"));
const NewHomeShell = lazy(() => import("./v2/shell/HomeShell"));
const ProjectsPage = lazy(() => import("./v2/pages/ProjectsPage"));
const AccountSecurityPage = lazy(() => import("./v2/pages/AccountSecurityPage"));
const NewProjectPage = lazy(() => import("./v2/pages/NewProjectPage"));
const ChangePlanPage = lazy(() => import("./v2/pages/ChangePlanPage"));
const ServicePage = lazy(() => import("./v2/pages/ServicePage"));
const OverviewPage = lazy(() => import("./v2/pages/OverviewPage"));
const AssistantPage = lazy(() => import("./v2/pages/AssistantPage"));
const ServicesPage = lazy(() => import("./v2/pages/ServicesPage"));
const SignalsPage = lazy(() => import("./v2/pages/SignalsPage"));
const EnvironmentsPage = lazy(() => import("./v2/pages/EnvironmentsPage"));
const PipelinesPage = lazy(() => import("./v2/pages/PipelinesPage"));
const PipelinePage = lazy(() => import("./v2/pages/PipelinePage"));
const RunPage = lazy(() => import("./v2/pages/RunPage"));
const DeployPage = lazy(() => import("./v2/pages/DeployPage"));
const ServiceVariablesTab = lazy(() => import("./v2/pages/settings/ServiceVariablesTab"));
const ComponentConfigTab = lazy(() => import("./v2/pages/settings/ComponentConfigTab"));
const ComponentAdvancedTab = lazy(() => import("./v2/pages/settings/ComponentConfigTab").then(m => ({ default: () => <m.default categories={["advanced"]} title="Advanced" /> })));
const ServiceDomainTab = lazy(() => import("./v2/pages/settings/ServiceDomainTab"));
const ServiceDangerTab = lazy(() => import("./v2/pages/settings/ServiceDangerTab"));
const ServiceScalingTab = lazy(() => import("./v2/pages/settings/ServiceScalingTab"));
const ServicePipelineTab = lazy(() => import("./v2/pages/settings/PipelineTabs").then(m => ({ default: m.ServicePipelineTab })));
const ComponentPipelineTab = lazy(() => import("./v2/pages/settings/PipelineTabs").then(m => ({ default: m.ComponentPipelineTab })));
const DeploymentsPage = lazy(() => import("./v2/pages/DeploymentsPage"));
const AuthShell = lazy(() => import("./v2/pages/auth/AuthShell"));
const LoginPage = lazy(() => import("./v2/pages/auth/LoginPage"));
const SignupPage = lazy(() => import("./v2/pages/auth/SignupPage"));
const EmailVerifiedPage = lazy(() => import("./v2/pages/auth/EmailVerifiedPage"));
const SetPasswordPage = lazy(() => import("./v2/pages/auth/SetPasswordPage"));
const ResetPasswordRequestPage = lazy(() => import("./v2/pages/auth/ResetPasswordRequestPage"));
const ResetPasswordPage = lazy(() => import("./v2/pages/auth/ResetPasswordPage"));
const ActivationRequestPage = lazy(() => import("./v2/pages/auth/ActivationRequestPage"));
const ActivityPage = lazy(() => import("./v2/pages/ActivityPage"));
const SettingsRoot = lazy(() => import("./v2/pages/SettingsRoot"));
const IntegrationsPage = lazy(() => import("./v2/pages/IntegrationsPage"));
const TemplatesPage = lazy(() => import("./v2/pages/TemplatesPage"));
const MembersPage = lazy(() => import("./v2/pages/settings/MembersPage"));
const GeneralPage = lazy(() => import("./v2/pages/settings/GeneralPage"));
const UsagePage = lazy(() => import("./v2/pages/settings/UsagePage"));
const SecurityPage = lazy(() => import("./v2/pages/settings/SecurityPage"));
const BillingPage = lazy(() => import("./v2/pages/settings/BillingPage"));
const ApiKeysPage = lazy(() => import("./v2/pages/settings/ApiKeysPage"));
const EnvironmentSettingsPage = lazy(() => import("./v2/pages/EnvironmentSettingsPage"));
const ComponentPage = lazy(() => import("./v2/pages/ComponentPage"));
const AddPage = lazy(() => import("./v2/pages/AddPage"));
const CostsPage = lazy(() => import("./v2/pages/CostsPage"));
const EntitySettingsLayout = lazy(() => import("./v2/pages/EntitySettingsLayout"));
const ProjectLayout = () => {
  // New project, pricing and not-found pages share this layout but belong to no project
  const { projectId } = useParams();
  if (!isNewUI()) return <ProjectPageLayout />;
  return <Suspense fallback={null}>{projectId ? <NewProjectShell /> : <NewHomeShell />}</Suspense>;
};
// New page when the new UI is on, otherwise the classic one (or a redirect to it)
const v2 = (Page: ComponentType, classic: ReactElement) => isNewUI()
  ? <Suspense fallback={null}><Page /></Suspense>
  : classic;
// Sign-in, sign-up and recovery: the new UI's welcome layout, or the classic one
const authShell = () => isNewUI() ? <Suspense fallback={null}><AuthShell /></Suspense> : <AuthRoot />;
// New project, or a new plan for the current project (activate a lapsed one, or switch a running one)
const Pricing = () => {
  const { action } = useParams();
  return v2(action === "new" ? NewProjectPage : ChangePlanPage, <PricingPlans />);
};
// The classic second step (?plan=) lands on the same page with the plan picked
const NewProject = () => {
  const { search } = useLocation();
  return isNewUI() ? <Navigate replace to={`/pricing/new${search}`} /> : <ProjectNew />;
};
// The classic "deploy template" pages; in the new UI the starters it has a flow for open there
const ClassicTemplate = ({ children }: { children: ReactElement }) => {
  const { projectId, templateId, envId } = useParams();
  const to = isNewUI() && projectId && templateId ? starterAddLink(projectId, templateId, envId) : undefined;
  return to ? <Navigate replace to={to} /> : children;
};
const ToProject = ({ to }: { to: string }) => {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}/${to}`} replace />;
};

// In the new UI an environment's page is its Services map, and components have their own page.
// App pages under an environment still need the classic layout, which provides their data.
const usesClassicEnvLayout = (pathname: string) => /\/apps\/|\/active-templates$/.test(pathname);
// Kubernetes app tabs live on the new service page: …/components/:c/apps/:namespace/:app/:section
const APP_TAB = /\/environments\/([^/]+)\/components\/[^/]+\/apps\/[^/]+\/([^/]+)\/(overview|logs|pipelines|metrics|incidents)$/;
const NewEnvLayout = () => {
  const { pathname } = useLocation();
  const { projectId } = useParams();
  const tab = APP_TAB.exec(pathname);
  if (tab) {
    const [, envId, app, section] = tab;
    return <Navigate replace to={`/projects/${projectId}/services/${app}?env=${envId}${section === "overview" ? "" : `&tab=${section}`}`} />;
  }
  return usesClassicEnvLayout(pathname) ? <EnvironmentRoot /> : <Outlet />;
};
const NewEnvIndex = () => {
  const { pathname } = useLocation();
  return usesClassicEnvLayout(pathname) ? <EnvironmentOverview /> : <Outlet />;
};
// The bare /environments/:envId; useOutlet can't tell an empty outlet apart in this router version
const EnvToMap = () => {
  const { projectId, envId } = useParams();
  return <Navigate replace to={`/projects/${projectId}/services?env=${encodeURIComponent(envId!)}`} />;
};

const App = () => {
  // Configure notification component
  notification.config({
    duration: 6
  });

  // Redirect to the old portal if state = redirect:portal
  const params = new URLSearchParams(window.location.search);
  const state = params.get("state") || "";
  const code = params.get("code") || "";
  const type = params.get("type") || "";

  if (state === "redirect:portal") {
    window.location.href = `${env.REACT_APP_OLD_PORTAL_URL}/login?code=${code}&state=${state}&type=${type}`;
  } else if (state === "redirect:ai") {
    window.location.href = `${env.REACT_APP_AI_CONSOLE_URL}/connect/git?code=${code}&state=${state}&type=${type}`;
  }
  //

  // Set default plan
  const defaultPlan = params.get("default_plan");
  if (defaultPlan) {
    setDefaultPlan(defaultPlan);
  }

  useEffect(() => {
    const onOffline = () => notification.error({ message: "No internet connection", description: "Please check your connection." });
    const onOnline = () => notification.success({ message: "Internet connection is back online" });

    window.addEventListener('offline', onOffline);
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener("online", onOnline);
    }
  }, []);

  return (
    <Router>
      <MetaTag defaultTags={true} />
      <Routes>
        <Route element={<PublicOutlet />}>
          <Route path='/' element={<Navigate to="/auth/login" replace />} />
          <Route path="auth" element={authShell()}>
            <Route path="login" element={v2(LoginPage, <Login />)} />
            <Route path="signup" element={v2(SignupPage, <Signup />)} />
            <Route path="signup/verify/:email/:code" element={v2(EmailVerifiedPage, <EmailVerify />)} />
            <Route path="set-password/:email" element={v2(SetPasswordPage, <SetPassword />)} />
            <Route path="reset-password" element={v2(ResetPasswordRequestPage, <ResetPasswordRequest />)} />
            <Route path="reset-password/:email/:code" element={v2(ResetPasswordPage, <ResetPassword />)} />
            <Route path="activate-account" element={v2(ActivationRequestPage, <ActivationRequest />)} />
          </Route>
          <Route element={<HomePageLayout />}>
            {/* <Route path="templates" element={<Templates />} /> */}
            <Route path="templates/new" element={<CreateFromTemplate />} />
          </Route>

          {/* Backward compatibility with the old implementation */}
          <Route path="/" element={authShell()}>
            <Route path="login" element={v2(LoginPage, <Login />)} />
            <Route path="signup" element={v2(SignupPage, <Signup />)} />
            <Route path="account/verify/:email/:code" element={v2(EmailVerifiedPage, <EmailVerify />)} />
            <Route path="set-password/:email" element={v2(SetPasswordPage, <SetPassword />)} />
            <Route path="reset-password" element={v2(ResetPasswordRequestPage, <ResetPasswordRequest />)} />
            <Route path="account/reset/:email/:code" element={v2(ResetPasswordPage, <ResetPassword />)} />
            <Route path="activate-account" element={v2(ActivationRequestPage, <ActivationRequest />)} />
          </Route>
        </Route>
        <Route element={<PrivateOutlet />}>
          {/* Projects */}
          <Route element={isNewUI() ? <Suspense fallback={null}><NewHomeShell /></Suspense> : <HomePageLayout />}>
            <Route path="account/security" element={v2(AccountSecurityPage, <AccountSecurity />)} />
            <Route path="projects" element={isNewUI() ? <Outlet /> : <ProjectsRoot />} >
              <Route index element={v2(ProjectsPage, <ProjectsOverview />)} />
            </Route>
            <Route path="welcome" element={<Welcome />} />
          </Route>

          {/* Project Root */}
          <Route element={<ProjectLayout />}>
            {/* Project Environments */}
            <Route path="projects/:projectId" element={<ProjectRoot />}>
              <Route index element={isNewUI() ? <ToProject to="overview" /> : <ProjectEnvironments />} />
              <Route path="environments" element={v2(EnvironmentsPage, <ProjectEnvironments />)} />
              <Route path="overview" element={v2(OverviewPage, <ToProject to="environments" />)} />
              <Route path="assistant" element={v2(AssistantPage, <ToProject to="environments" />)} />
              <Route path="assistant/:chatId" element={v2(AssistantPage, <ToProject to="environments" />)} />
              <Route path="services" element={v2(ServicesPage, <ToProject to="environments" />)} />
              <Route path="services/:serviceName" element={v2(ServicePage, <ToProject to="environments" />)} />
              <Route path="add" element={v2(AddPage, <ToProject to="environments" />)} />
              <Route path="signals" element={v2(SignalsPage, <ToProject to="monitoring/alarms" />)} />
            </Route>

            {/* Project Deployments */}
            <Route path="projects/:projectId">
              <Route path="pipelines" element={v2(PipelinesPage, <ProjectPipelines />)} />
              {/* The classic Pipelines page is a feed of builds and deploys; the new UI splits it into Deployments and Pipelines */}
              <Route path="activity" element={v2(ActivityPage, <ToProject to="pipelines" />)} />
              <Route path="deployments" element={v2(DeploymentsPage, <ToProject to="pipelines" />)} />
              <Route path="deployments/:envId/:deploymentId" element={v2(DeployPage, <ToProject to="pipelines" />)} />
              <Route path="pipelines/:pipelineId" element={v2(PipelinePage, <ToProject to="pipelines" />)} />
              <Route path="pipelines/:pipelineId/runs/:runId" element={v2(RunPage, <ToProject to="pipelines" />)} />
              <Route path="monitoring" element={<ProjectMonitoring />} />
              <Route path=":envId/:clusterId/:appName/monitoring" element={<ProjectMonitoring />} />
              <Route path=":envId/:clusterId/:namespace/:appName/monitoring" element={<ProjectMonitoring />} />
              <Route path="monitoring/login" element={<ProjectMonitoringLogin />} />
              <Route path="monitoring/alarms" element={isNewUI() ? <ToProject to="signals" /> : <ProjectAlarms />} />
              <Route path="monitoring/:clusterType/alarm/:metricGroup" element={<ProjectCreateAlarm />} />
            </Route>

            {/* Project Other */}
            <Route path="projects/:projectId">
              <Route path="cost-explorer" element={v2(CostsPage, <CostManagement />)} />
              <Route path="templates" element={v2(TemplatesPage, <Templates />)} />
              {/* One page in the new UI; the tab paths below are the classic UI's and show the same page there */}
              <Route path="integrations" element={v2(IntegrationsPage, <ProjectIntegrationsRoot />)}>
                <Route index element={isNewUI() ? null : <ProjectIntegrationsGit />} />
                <Route path="git" element={<ProjectIntegrationsGit />} />
                <Route path="cloud" element={<ProjectIntegrationsCloud />} />
                <Route path="container" element={<ProjectIntegrationsContainer />} />
                <Route path="slack" element={<ProjectIntegrationsSlack />} />
              </Route>
              <Route path="settings" element={v2(SettingsRoot, <ProjectSettingsRoot />)}>
                <Route index element={isNewUI() ? <ToProject to="settings/general" /> : <ProjectSettingsGeneral />} />
                <Route path="general" element={v2(GeneralPage, <ProjectSettingsGeneral />)} />
                <Route path="usage" element={v2(UsagePage, <ProjectSettingsUsage />)} />
                <Route path="members" element={v2(MembersPage, <ProjectSettingsMembers />)} />
                <Route path="security" element={v2(SecurityPage, <ProjectSettingsSecurity />)} />
                <Route path="api-keys" element={v2(ApiKeysPage, <ProjectSettingsApiKeys />)} />
                <Route path="billing" element={v2(BillingPage, <ProjectSettingsBilling />)} />
              </Route>
            </Route>

            {/* <Route path="templates" element={<Templates />} /> */}
            <Route path="projects/new" element={<NewProject />} />
            <Route path="templates/new" element={<CreateFromTemplate />} />

            <Route path="projects/:projectId">
              {/* Environments */}
              <Route path="environments/:envId" element={isNewUI() ? <NewEnvLayout /> : <EnvironmentRoot />}>
                {/* Several layouts share this path; an index route makes the bare one unambiguous */}
                {isNewUI() && <Route index element={<EnvToMap />} />}
                <Route path="" element={isNewUI() ? <NewEnvIndex /> : <EnvironmentOverview />}>
                  <Route path="components/:componentId/apps/:appName/:section" element={<EnvironmentFargateAppDetails />} />
                  <Route path="components/:componentId/apps/:namespace/:appName/:section" element={<EnvironmentKubernetesAppDetails />} />
                  <Route path="components/:componentId/:section" element={v2(ComponentPage, <EnvironmentComponentDetails />)} />
                </Route>
                {/* <Route path="deployments/infrastructure/:deploymentId" element={<EnvironmentInfraDeploymentDetails />} /> */}
                {/* <Route path="deployments/apps/:clusterId/:namespace/:appName/:deploymentId" element={<EnvironmentAppDeploymentDetails />} /> */}
                <Route path="active-templates" element={<EnvironmentRunningTemplates />} />
              </Route>

              {/* Preview Playground */}
              <Route path="environments/:envId" element={<EnvironmentRoot previewPlayground={true} />}>
                <Route path="preview-playground" element={<EnvironmentPreviewPlayground />} />
              </Route>

              {/* Environment settings */}
              {/* The new UI's settings page has its own header, so it skips EnvironmentRoot's */}
              <Route path="environments/:envId" element={isNewUI() ? <Outlet /> : <EnvironmentRoot settings={true} />}>
                <Route path="settings" element={v2(EnvironmentSettingsPage, <EnvironmentSettingsRoot />)}>
                  <Route index element={<EnvironmentSettingsGeneral />} />
                  <Route path="general" element={<EnvironmentSettingsGeneral />} />
                  <Route path="clone" element={<EnvironmentSettingsClone />} />
                  <Route path="danger-zone" element={<EnvironmentSettingsDangerZone />} />
                </Route>
              </Route>

              {/* App settings */}
              <Route path="environments/:envId" element={v2(EntitySettingsLayout, <EnvironmentRoot settings={true} />)}>
                <Route path="components/:componentId/:clusterId/apps/:namespace/:appName/settings" element={<AppSettingsRoot />}>
                  <Route index element={v2(ServiceVariablesTab, <AppSettingsEnvironmentVars />)} />
                  <Route path="environment" element={v2(ServiceVariablesTab, <AppSettingsEnvironmentVars />)} />
                  <Route path="domain" element={v2(ServiceDomainTab, <AppSettingsCustomDomain />)} />
                  <Route path="pipeline" element={v2(ServicePipelineTab, <AppSettingsPipeline />)} />
                  <Route path="scaling" element={v2(ServiceScalingTab, <AppSettingsScaling />)} />
                  <Route path="danger-zone" element={v2(ServiceDangerTab, <AppSettingsDangerZone />)} />
                </Route>
              </Route>

              {/* Component settings */}
              <Route path="environments/:envId" element={v2(EntitySettingsLayout, <EnvironmentRoot settings={true} />)}>
                {/* Standalone component */}
                <Route path="components/:componentId/settings" element={<ResourceSettingsRoot />}>
                  <Route index element={v2(ComponentConfigTab, <ResourceSettingsGeneral />)} />
                  <Route path="general" element={v2(ComponentConfigTab, <ResourceSettingsGeneral />)} />
                  <Route path="pipeline" element={v2(ComponentPipelineTab, <ResourceSettingsPipeline />)} />
                  <Route path="scaling" element={<ResourceSettingsScaling />} />
                  <Route path="alarms" element={<ResourceSettingsAlarms />} />
                  <Route path="domain" element={<ResourceSettingsCustomDomain />} />
                  <Route path="storage" element={<ResourceSettingsStorage />} />
                  <Route path="variables" element={<ResourceSettingsVariables />} />
                  <Route path="advanced" element={v2(ComponentAdvancedTab, <ResourceSettingsGeneral filterCategories={["advanced"]} />)} />
                  <Route path="danger-zone" element={<ResourceSettingsDangerZone />} />
                </Route>
                {/* Cluster */}
                <Route path="components/:componentId/:clusterId/settings" element={<ResourceSettingsRoot />}>
                  <Route index element={v2(ComponentConfigTab, <ResourceSettingsGeneral />)} />
                  <Route path="general" element={v2(ComponentConfigTab, <ResourceSettingsGeneral />)} />
                  <Route path="pipeline" element={v2(ComponentPipelineTab, <ResourceSettingsPipeline />)} />
                  <Route path="scaling" element={<ResourceSettingsScaling />} />
                  <Route path="alarms" element={<ResourceSettingsAlarms />} />
                  <Route path="domain" element={<ResourceSettingsCustomDomain />} />
                  <Route path="storage" element={<ResourceSettingsStorage />} />
                  <Route path="variables" element={<ResourceSettingsVariables />} />
                  <Route path="monitoring" element={<ClusterMonitoringSettings />} />
                  <Route path="advanced" element={v2(ComponentAdvancedTab, <ResourceSettingsGeneral filterCategories={["advanced"]} />)} />
                  <Route path="danger-zone" element={<ResourceSettingsDangerZone />} />
                </Route>
                <Route path="components/:clusterId/kubernetes/settings" element={<KubernetesSettingsRoot />}>
                  <Route index element={<KubernetesSettingsGeneral />} />
                  <Route path="general" element={<KubernetesSettingsGeneral />} />
                  <Route path="monitoring" element={<ClusterMonitoringSettings />} />
                  <Route path="danger-zone" element={<KubernetesSettingsDangerZone />} />
                </Route>
              </Route>

              {/* Create from Templates */}
              <Route path="environments/:envId" element={<EnvironmentRoot templates={true} />}>
                <Route path="templates/:templateId" element={<ClassicTemplate><CreateFromTemplate hideHeader={true} /></ClassicTemplate>} />
                <Route path="components/:componentId/:clusterId/templates/:templateId" element={<CreateFromTemplate hideHeader={true} />} />
              </Route>

              {/* Add existing Component in environment */}
              <Route path="environments/:envId" element={<EnvironmentRoot existingTemplate={true} />}>
                <Route path="templates/:templateId/add-component" element={<EnvironmentDeployComponent />} />
              </Route>

              {/* Add existing App in environment */}
              <Route path="environments/:envId" element={<EnvironmentRoot existingTemplate={true} />}>
                <Route path="templates/:templateId/add-app" element={<EnvironmentDeployApp />} />
                <Route path="components/:componentId/:clusterId/templates/:templateId/add-app" element={<EnvironmentDeployApp />} />
              </Route>

              {/* Apps */}
              <Route path="environments/:envId/apps/:clusterId/:namespace/:appName" element={<AppRoot />}>
                <Route path="pipeline" element={<AppPipelineBuilds />} />
                <Route path="pipeline/:pipelineId/build/:buildId" element={<PipelineBuildDetails />} />
                <Route path="settings" element={<AppSettingsRoot />}>
                  <Route index element={v2(ServiceVariablesTab, <AppSettingsEnvironmentVars />)} />
                  <Route path="environment" element={v2(ServiceVariablesTab, <AppSettingsEnvironmentVars />)} />
                  <Route path="domain" element={v2(ServiceDomainTab, <AppSettingsCustomDomain />)} />
                  <Route path="pipeline" element={v2(ServicePipelineTab, <AppSettingsPipeline />)} />
                  <Route path="scaling" element={v2(ServiceScalingTab, <AppSettingsScaling />)} />
                  <Route path="deployments" element={<AppSettingsDeployments />} />
                  <Route path="danger-zone" element={v2(ServiceDangerTab, <AppSettingsDangerZone />)} />
                </Route>
              </Route>

              {/* Templates */}
              <Route path="templates" element={<TemplateRoot />}>
                <Route path=":templateId" element={<ClassicTemplate><CreateFromTemplate hideHeader={true} /></ClassicTemplate>} />
              </Route>
              <Route path="templates/components/:componentId/details" element={<TemplateComponentDetails />} />
              <Route path="templates/apps/:appId/details" element={<TemplateAppDetails />} />
            </Route>

            <Route path="/pricing/:action" element={<Pricing />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Route>
      </Routes>
    </Router >
  );
};

export default App;
